import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiTrash2, FiCopy } from "react-icons/fi"
import { getChildren } from "../helpers";
import { useToast } from "../../../../../helpers/commonFunction";
import { myRedoStack, myUndoStack } from "../stack/Stack";
import { DeviceType, FormCreationData } from "../../../../../stateManager/store/formCreation_v2/types";
import { getComponents } from '../helpers/assetHelpers'
import { setFixedElements, pushFormActionStack, removeElement, undoDeleteElement } from "../../../../../stateManager/store/formCreation_v2/actions";
import { IoIosCloseCircle } from "react-icons/io"
import { useTopElement } from "../hooks/useTopElement";
import { AppState } from "../../../../../stateManager/store";
import { getDimensions } from "../helpers/getDimensions";
import {deleteSelectedSegmentId} from "../../../../../stateManager/store/segmentControl/actions";
import { getMargin } from "../helpers/generateStyles";

interface IDeleteElement {
  id: string;
  parentId?: string;
  selectedElementId: string;
  type: string;
  properties: any; 
}

const getWidth = (deviceType: DeviceType): string => {
  switch (deviceType) {
    case "Sm":
      return "57.27px"
    case "Md":
      return "65px"
    case "Lg":
      return "65px"
    default:
      return ""
  }
}

const getPosition = (deviceType: DeviceType): string => {
  switch (deviceType) {
    case "Sm":
      return "-26px"
    case "Md":
      return "-28px"
    case "Lg":
      return "-28px";
    default:
        return "";
  }
}

export const DeleteElement: React.FC<IDeleteElement> = ({
  id,
  parentId,
  selectedElementId,
  type,
  properties
}) => {
  const [isTop, setIsTop] = useTopElement();
  const dispatch = useDispatch();
  const { components, componentOrder } = getComponents()
  const { deviceType } = useSelector((state: AppState) => state.formJson);
  const component: FormCreationData = components[selectedElementId] || {};
  const fixedArray = useSelector((state: any) => state.formJson.fixedArray) || []
  const value = component?.properties?.styles?.[deviceType]?.fixed || false

  useEffect(() => {
    setIsTop(id)
  }, [id, setIsTop])

  function getNestedComponents(fields: string[], array: FormCreationData[] = []) {
    fields.forEach((fieldId: string) => {
      const component = components[fieldId];
      array.push(component);
      // recursively all fields
      if (component?.fields?.length) {
        getNestedComponents(component.fields, array);
      }
    })
  }

  const { SuccessToast } = useToast()

  const removeSelectedElement = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    if (component.type === "SegmentControl") {
      dispatch(deleteSelectedSegmentId(id))
    }
    const childrenArray: FormCreationData[] = [];
    getNestedComponents(component?.fields, childrenArray);
    const payload: FormCreationData = {
      ...component,
      id,
      parentId: parentId || "",
      type,
      children: childrenArray,

    }

    if (parentId) {
      payload.metadata = {
        position: components[parentId].fields.indexOf(id)
      }
    }
    // Undo Redo for delete elements
    myUndoStack.push({ ...payload });
    // component order
    myUndoStack.saveOrder(componentOrder);
    const order = myUndoStack.getOrder();

    dispatch(pushFormActionStack({
      undo: () => {
        const poppedElement = myUndoStack.pop();
        if (poppedElement) {
          myRedoStack.push(poppedElement);
          dispatch(
            undoDeleteElement(poppedElement, order),
          )
        }
      },
      redo: () => {
        const poppedElement = myRedoStack.pop();
        if (poppedElement) {
          myUndoStack.push({ ...poppedElement });
          dispatch(removeElement({
            id: poppedElement.id,
            parentId: poppedElement.parentId || "",
            type: poppedElement.type,
            increment: 1
          }))
        }
      }
    }));

    dispatch(
      removeElement({
        id,
        parentId,
        type,
      })
    );
    if (value) {
      const updatedFixedArray = fixedArray.filter((ele: string) => ele !== selectedElementId)
      dispatch(setFixedElements(updatedFixedArray))
    }
  };

  const handleCopy = async (e: React.MouseEvent<SVGElement, MouseEvent>) => {
    e.stopPropagation();
    const rootComponent = { ...components[selectedElementId] }
    const copiedComponents: any = {
      [selectedElementId]: rootComponent
    }

    const componentIds = getChildren(rootComponent.fields, components)
    componentIds.forEach(id => {
      copiedComponents[id] = { ...components[id] }
    })

    const data = {
      componentOrder: [selectedElementId],
      components: copiedComponents,
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(data));
      SuccessToast("Copied")
    } catch (err) {
      console.error("failed to copy text: ", err);
    }
  };

  const elementStyle = properties?.styles || {};
  const deviceStyle = elementStyle[deviceType];
  const marginStyle = deviceStyle ? getMargin(deviceStyle) : {};

  const style: React.CSSProperties = {
    position: "absolute",
    right: 0,
    bottom: 0,
    display: id === selectedElementId ? "flex" : "none",
    fontWeight: 700,
    height: "24px",
    alignItems: "center",
    background: "#e7e7e7",
    width:  deviceType ? getWidth(deviceType) : "",
    justifyContent: "space-evenly",
    padding: "4px",
    zIndex: 100,
    borderRadius: "4px",
    ...marginStyle
  };

  const getIconSize = (): number => {
    switch (deviceType) {
      case "Sm":
        return 14
      case "Md":
        return 20
      case "Lg":
        return 20
      default:
        return 14
    }
  }

  return (
    <>
      {type !== "FAB" && type !== "SnackBar" && type !== "A8Modal" && <div style={style}>
        <FiCopy size={getIconSize()} color="#121212" style={{ cursor: "pointer" }} onClick={handleCopy} />
        <FiTrash2 size={getIconSize()} color="#F32727" style={{ cursor: "pointer" }} onClick={(e: any) => removeSelectedElement(e, id)} />
      </div>}
    </>
  );
};
